// Batch 23 · Stage 6(b) — the "Full screen" mode: the sidebar and the top bar hide so a page uses the whole browser window.
// The choice is remembered per user in this browser (localStorage, every access wrapped: a blocked store must never break a
// page — the mode then simply starts off). It changes no data and no URL.
import { useCallback, useEffect, useState } from "react";

export const FULL_SCREEN_EVENT = "azex:fullscreen";
export const storageKey = (userId) => `azex.fullscreen.${userId || "anon"}`;

export function readFullScreen(userId) {
  try { return window.localStorage.getItem(storageKey(userId)) === "1"; } catch { return false; }
}

export function writeFullScreen(userId, on) {
  try { window.localStorage.setItem(storageKey(userId), on ? "1" : "0"); } catch { /* storage blocked: the mode still works for this page view */ }
}

/** [on, setOn, toggle] — Esc turns the mode off (unless a dialog / menu took the key first). */
export function useFullScreenMode(userId) {
  const [on, setOnState] = useState(() => readFullScreen(userId));
  useEffect(() => { setOnState(readFullScreen(userId)); }, [userId]);
  const setOn = useCallback((v) => {
    setOnState(v); writeFullScreen(userId, v);
    try { window.dispatchEvent(new CustomEvent(FULL_SCREEN_EVENT, { detail: { on: v } })); } catch { /* ignore */ }
  }, [userId]);
  useEffect(() => {
    if (!on) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (document.querySelector("[role=dialog], [role=alertdialog], [role=menu], [role=listbox]")) return;   // Esc belongs to the open dialog
      setOn(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [on, setOn]);
  const toggle = useCallback(() => setOn(!on), [on, setOn]);
  return [on, setOn, toggle];
}

/** The browser's own full screen (F11-like) — offered as a second button where the browser allows it; never relied on. */
export function browserFullScreenAvailable() {
  try { return !!(document.fullscreenEnabled && document.documentElement.requestFullscreen); } catch { return false; }
}

export async function toggleBrowserFullScreen() {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await document.documentElement.requestFullscreen();
  } catch { /* refused by the browser: nothing to do */ }
}
